<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row" v-if="!!transfer.id">
          <div class="col-8">
            <h3 class="mb-0 view-header-buttons">
              {{ transfer.seq_number ?? transfer.code }}
              | {{ transfer.sourceEstablishment.company_name }} =>
              {{ transfer.destinationEstablishment.company_name }}
              <base-button
                class="mr-2"
                type="primary"
                size="sm"
                @click="goBack"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
              <br/><br/>
              <base-button
                v-if="$currentUserCan($permissions.PERM_EDIT_EQUIPMENT_TRANSFERS) && transfer.status === 'DRAFT'"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :disabled="loading"
                @click="goToEdit"
              >
                <i class="fas fa-edit"></i>
                {{ $t("COMMON.EDIT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'DRAFT' && transfer.items.length > 0"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :loading="loading"
                @click="validate"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("EQUIPMENT_TRANSFERS.SUBMIT") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'DRAFT' &&
                  $currentUserCan($permissions.PERM_DELETE_EQUIPMENT_TRANSFERS)
                "
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="deleteTransfer"
              >
                <i class="fas fa-trash"></i>
                {{ $t("COMMON.DELETE") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="success"
                icon
                size="sm"
                :loading="loading"
                @click="accept"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("EQUIPMENT_TRANSFERS.ACCEPT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :loading="loading"
                @click="reject"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("EQUIPMENT_TRANSFERS.REJECT") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'DRAFT' ||
                  transfer.status === 'SUBMITTED'
                "
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="cancel"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("COMMON.CANCEL") }}
              </base-button>
              <base-button
                v-if="canRevertToDraft"
                class="mr-2"
                type="secondary"
                icon
                size="sm"
                :loading="loading"
                @click="revertToDraft"
              >
                <i class="ni ni-curved-next"></i>
                {{ $t("EQUIPMENT_TRANSFERS.REVERT_TO_DRAFT") }}
              </base-button>
              <base-button
                v-if="canReturnAll"
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :loading="loading"
                @click="returnAll"
              >
                <i class="ni ni-curved-next"></i>
                {{ $t("EQUIPMENT_TRANSFERS.RETURN_ALL") }}
              </base-button>
              <base-button
                v-if="canPrint"
                class="mr-2"
                type="info"
                icon
                size="sm"
                :disabled="loading"
                @click="print"
              >
                <i class="fa fa-file-pdf"></i>
                {{ $t("COMMON.PRINT") }}
              </base-button>
              <base-button
                v-if="canSendMail"
                class="mr-2"
                type="success"
                icon
                size="sm"
                @click="toggleMailModal"
              >
                <i class="fas fa-envelope"></i>
                {{ $t("EQUIPMENT_TRANSFERS.SEND_BY_MAIL") }}
              </base-button>
            </h3>
            <!-- The author cannot accept nor reject their own transfer: say so rather
                 than leaving an empty toolbar. -->
            <div
              class="alert alert-secondary mb-0"
              v-if="isSubmitter && transfer.status === 'SUBMITTED'"
            >
              <i class="fas fa-info-circle mr-1"></i>
              {{ $t("EQUIPMENT_TRANSFERS.SUBMITTER_CANNOT_DECIDE") }}
            </div>
          </div>
        </div>
      </template>

      <div
        class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
      >
        <tabs
          fill
          class="flex-column flex-md-row"
          tabNavWrapperClasses="nav-wrapper"
          tabNavClasses="nav nav-pills nav-fill"
          value="details"
        >
          <card shadow>
            <tab-pane title="global" id="1" :active="true">
              <span slot="title">
                <i class="ni ni-cloud-upload-96" />
                {{ $t("COMMON.GLOBAL") }}
              </span>
              <equipment-transfer-view-global :transfer="transfer" />
            </tab-pane>

            <tab-pane title="details" id="2" :active="false">
              <span slot="title">
                <i class="ni ni-bullet-list-67" />
                {{ $t("COMMON.DETAILS") }}
              </span>
              <equipment-transfer-items-table
                :transfer="transfer"
                @onSubmitTransferItem="handleItemSubmit"
                @onDeleteTransferItem="handleItemDelete"
                @onReturnItem="handleReturnItem"
                @onTransferUpdated="refreshTransfer"
              />
            </tab-pane>
          </card>
        </tabs>
      </div>
    </card>

    <modal
      v-if="showMailModal"
      :show="showMailModal"
      modal-classes="modal-secondary"
      size="xl"
    >
      <equipment-transfer-mail-modal-content
        :transfer="transfer"
        :toggleMailModal="toggleMailModal"
      />
    </modal>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import defaultTransfer from "./defaultEquipmentTransfer";
import EquipmentTransferItemsTable from "./partials/EquipmentTransferItemsTable.vue";
import EquipmentTransferViewGlobal from "./partials/EquipmentTransferViewGlobal.vue";
import EquipmentTransferMailModalContent from "./partials/EquipmentTransferMailModalContent.vue";
import defaultTransferItem from "./defaultEquipmentTransferItem";
import { Tabs, TabPane } from "@/components";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { openPdfBlob } from "@/helpers/downloadPdf";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";

export default {
  name: "ViewEquipmentTransferPage",
  components: {
    EquipmentTransferItemsTable,
    EquipmentTransferViewGlobal,
    EquipmentTransferMailModalContent,
    Tabs,
    TabPane,
  },
  mixins: [requestErrorMixin],
  data() {
    return {
      transfer: cloneDeep(defaultTransfer),
      loading: false,
      transferItem: cloneDeep(defaultTransferItem),
      showMailModal: false,
    };
  },
  created() {
    this.getTransfer();
    if (!this.$store.getters["profile/me"]) {
      this.$store.dispatch("profile/me");
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["profile/me"];
    },
    isSubmitter() {
      if (!this.currentUser || !this.transfer?.submittedBy) {
        return false;
      }
      return String(this.currentUser.id) === String(this.transfer.submittedBy.id);
    },
    /**
     * Whoever wrote the transfer — created it or submitted it. Mirrors
     * EquipmentTransfer::wasAuthoredBy on the API side.
     */
    isAuthor() {
      const me = this.currentUser;
      if (!me) return false;

      const authorIds = [
        this.transfer.createdBy?.id,
        this.transfer.submittedBy?.id,
      ].filter(Boolean);

      return authorIds.some((id) => String(id) === String(me.id));
    },
    /**
     * Pulling the transfer back into DRAFT is reserved to its author, and only
     * while no equipment has been committed (the API enforces the same rule).
     */
    canRevertToDraft() {
      return (
        ["SUBMITTED", "REJECTED", "CANCELED"].includes(this.transfer.status) &&
        this.isAuthor &&
        this.$currentUserCan(this.$permissions.PERM_EDIT_EQUIPMENT_TRANSFERS)
      );
    },
    canPrint() {
      return [
        "SUBMITTED",
        "ACCEPTED",
        "PARTIALLY_RETURNED",
        "RETURNED",
      ].includes(this.transfer.status);
    },
    canReturnAll() {
      return (
        this.transfer.transfer_type === "TEMPORARY" &&
        ["ACCEPTED", "PARTIALLY_RETURNED"].includes(this.transfer.status) &&
        this.$currentUserCan(this.$permissions.PERM_RETURN_EQUIPMENT_TRANSFERS)
      );
    },
    /**
     * The transfer can be e-mailed to the destination establishment as soon as it
     * left DRAFT and is still live (a rejected or canceled transfer has nothing to
     * send).
     */
    canSendMail() {
      return (
        !!this.transfer.id &&
        !["DRAFT", "REJECTED", "CANCELED"].includes(this.transfer.status) &&
        this.$currentUserCan(this.$permissions.PERM_VIEW_EQUIPMENT_TRANSFERS)
      );
    },
  },
  methods: {
    async getTransfer() {
      this.loading = true;
      try {
        await this.$store.dispatch("equipmentTransfers/get", {
          id: this.$route.params.id,
        });
        this.transfer = this.$store.getters["equipmentTransfers/transfer"];
      } finally {
        this.loading = false;
      }
    },
    async refreshTransfer() {
      await this.getTransfer();
    },
    /**
     * Submit the transfer, then offer to e-mail it right away — same two-step flow
     * as an internal order submission.
     */
    async validate() {
      if (!this.transfer.items || !this.transfer.items.length) {
        this.$notify({
          type: "danger",
          message: this.$t("EQUIPMENT_TRANSFERS.ADD_ITEMS_FIRST"),
        });
        return;
      }

      const confirmation = await this.confirm("SUBMIT_QUESTION");
      if (!confirmation) return;

      const submitted = await this.runAction(
        "validate",
        "EQUIPMENT_TRANSFER_SUBMITTED"
      );
      if (!submitted) return;

      const send = await swal.fire({
        title: this.$t("EQUIPMENT_TRANSFERS.SEND_NOW_QUESTION"),
        text: this.$t("EQUIPMENT_TRANSFERS.SEND_NOW_HINT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (send.value) {
        this.toggleMailModal();
      }
    },
    /**
     * Closing the modal refreshes the transfer so the "sent at / sent by" trace
     * shows up without a manual reload.
     */
    toggleMailModal() {
      this.showMailModal = !this.showMailModal;

      if (!this.showMailModal && this.transfer.id) {
        this.getTransfer();
      }
    },
    confirm(questionKey) {
      return swal
        .fire({
          title: this.$t(`EQUIPMENT_TRANSFERS.${questionKey}`),
          type: "question",
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: this.$t("COMMON.YES"),
          cancelButtonText: this.$t("COMMON.NO"),
          confirmButtonClass: "btn btn-primary",
          cancelButtonClass: "btn btn-warning",
        })
        .then((result) => !!result.value);
    },
    /**
     * Every workflow action goes through here: the backend answers with explicit
     * codes (ONLY_TRANSFER_AUTHOR_CAN_REVERT_TO_DRAFT, TRANSFER_HAS_NO_ITEMS, ...)
     * that showRequestError turns into a readable message.
     */
    async runAction(action, successKey) {
      this.loading = true;
      try {
        await this.$store.dispatch(`equipmentTransfers/${action}`, {
          id: this.transfer.id,
        });
        await this.getTransfer();
        this.$notify({
          type: "success",
          message: this.$t(`EQUIPMENT_TRANSFERS.${successKey}`),
        });
        return true;
      } catch (error) {
        await this.showRequestError(error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async accept() {
      if (!(await this.confirm("ACCEPT_QUESTION"))) return;

      await this.runAction("accept", "EQUIPMENT_TRANSFER_ACCEPTED");
    },
    async reject() {
      if (!(await this.confirm("REJECT_QUESTION"))) return;

      await this.runAction("reject", "EQUIPMENT_TRANSFER_REJECTED");
    },
    async cancel() {
      if (!(await this.confirm("CANCEL_QUESTION"))) return;

      await this.runAction("cancel", "EQUIPMENT_TRANSFER_CANCELED");
    },
    async revertToDraft() {
      if (!(await this.confirm("REVERT_TO_DRAFT_QUESTION"))) return;

      await this.runAction("revertToDraft", "EQUIPMENT_TRANSFER_REVERTED");
    },
    async returnAll() {
      if (!(await this.confirm("RETURN_ALL_QUESTION"))) return;

      await this.runAction("returnAll", "EQUIPMENT_TRANSFER_RETURNED");
    },
    async print() {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch(
          "equipmentTransfers/print",
          { id: this.transfer.id }
        );
        openPdfBlob(
          response,
          `${this.transfer.seq_number || this.transfer.code}.pdf`
        );
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    async handleItemSubmit(itemData) {
      if (!itemData.id) {
        await this.addTransferItem(itemData);
      } else {
        await this.updateTransferItem(itemData);
      }
      await this.refreshTransfer();
    },
    async addTransferItem(itemData) {
      const payload = {
        ...itemData,
        transfer: {
          type: "equipment-transfers",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("equipmentTransferItems/add", payload);
    },
    async updateTransferItem(itemData) {
      const payload = {
        ...itemData,
        transfer: {
          type: "equipment-transfers",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("equipmentTransferItems/update", payload);
    },
    async handleItemDelete(id) {
      await this.$store.dispatch("equipmentTransferItems/destroy", id);
      await this.refreshTransfer();
    },
    async handleReturnItem(id) {
      this.loading = true;
      try {
        await this.$store.dispatch("equipmentTransferItems/returnItem", {
          id,
        });
        await this.refreshTransfer();
        this.$notify({
          type: "success",
          message: this.$t("EQUIPMENT_TRANSFERS.EQUIPMENT_ITEM_RETURNED"),
        });
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    async deleteTransfer() {
      const confirmation = await swal.fire({
        title: this.$t("EQUIPMENT_TRANSFERS.DELETE_EQUIPMENT_TRANSFER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      this.loading = true;
      try {
        await this.$store.dispatch("equipmentTransfers/destroy", {
          id: this.transfer.id,
        });
        this.$notify({
          type: "success",
          message: this.$t("EQUIPMENT_TRANSFERS.DELETE_EQUIPMENT_TRANSFER"),
        });
        this.goBack();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    goToEdit() {
      this.$router.push({
        name: "Edit EquipmentTransfer",
        params: { id: this.transfer.id },
      });
    },
    goBack() {
      this.$router.push({ name: "List EquipmentTransfers" });
    },
  },
};
</script>
